import { useMemo, useState } from 'react';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

import { resetPassword } from '../features/auth';
import { useAuthStore } from '../store/authStore';
import { PaddiqLogo } from '@kangaroo/ui';
import { clearChatGptContinuation, readChatGptContinuation } from '../lib/chatGptConnection';

function getErrorMessage(error: unknown) {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message ?? 'Request failed';
  }
  return error instanceof Error ? error.message : 'Unknown error';
}

export function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const setSession = useAuthStore((state) => state.setSession);
  const token = useMemo(() => new URLSearchParams(location.search).get('token') ?? '', [location.search]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (!token) {
      setError('Reset link is missing.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await resetPassword({ token, password });
      await setSession(response.token, response.user);
      const connectionDestination = readChatGptContinuation(window.sessionStorage);
      if (connectionDestination) clearChatGptContinuation(window.sessionStorage);
      navigate(connectionDestination ?? '/drivers', { replace: true });
    } catch (submitError) {
      setError(getErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 3 }}>
      <Paper component="form" onSubmit={handleSubmit} sx={{ width: '100%', maxWidth: 420, p: 3 }}>
        <Stack spacing={2.5}>
          <Stack spacing={1} alignItems="center">
            <PaddiqLogo />
            <Typography variant="h5" fontWeight={800}>Choose a new password</Typography>
          </Stack>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField label="New password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} required fullWidth />
          <TextField label="Confirm password" type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required fullWidth />
          <Button type="submit" variant="contained" disabled={isSubmitting}>{isSubmitting ? 'Saving…' : 'Update password'}</Button>
          <Button component={RouterLink} to="/login" size="small">Back to sign in</Button>
        </Stack>
      </Paper>
    </Box>
  );
}
